import JSZip from "jszip";
import KML from "ol/format/KML.js";
import GPX from "ol/format/GPX.js";
import GeoJSON from "ol/format/GeoJSON.js";
import { parquetMetadataAsync, parquetSchema } from "hyparquet";
import ImportWorker from "worker-loader!./importedFileWorker.js";

export const IMPORT_LIMITS = Object.freeze({
  maxFileBytes: 100 * 1024 * 1024,
  maxExpandedBytes: 500 * 1024 * 1024,
  maxArchiveEntries: 10000,
  warningFeatures: 50000,
  maxFeatures: 200000,
  maxCoordinates: 5000000,
});

const geojsonFormat = new GeoJSON();
const normalizePath = value => {
  const parts = String(value || "").replace(/\\/g, "/").split("/");
  const normalized = [];
  parts.forEach(part => {
    if (!part || part === ".") return;
    if (part === "..") normalized.pop();
    else normalized.push(part);
  });
  return normalized.join("/");
};
const extensionOf = name => (String(name || "").toLowerCase().match(/\.[^.\/]+$/) || [""])[0];
const withoutExtension = name => String(name || "").replace(/\.[^.\/]+$/, "");
const datasetKey = value => normalizePath(value).replace(/[^a-zA-Z0-9._/-]/g, "_") || "dataset";

const asyncBuffer = arrayBuffer => ({
  byteLength: arrayBuffer.byteLength,
  slice(start, end) { return arrayBuffer.slice(start, end); },
});

let workerRequestId = 0;
const runBinaryWorker = (file, format, options) => new Promise((resolve, reject) => {
  const worker = new ImportWorker();
  const id = ++workerRequestId;
  const signal = options && options.signal;
  const workerOptions = { ...(options || {}) };
  delete workerOptions.signal;
  const onAbort = () => {
    finish();
    reject(new Error("Importación cancelada."));
  };
  const finish = () => {
    if (signal) signal.removeEventListener("abort", onAbort);
    worker.terminate();
  };
  if (signal?.aborted) return onAbort();
  if (signal) signal.addEventListener("abort", onAbort, { once: true });
  worker.onmessage = event => {
    if (event.data.id !== id) return;
    finish();
    if (event.data.error) reject(new Error(event.data.error));
    else resolve(event.data.result);
  };
  worker.onerror = error => {
    finish();
    reject(new Error(error.message || "Falló el proceso de importación."));
  };
  worker.postMessage({ id, file, format, options: workerOptions });
});

const ensureFileLimit = file => {
  if (file.size > IMPORT_LIMITS.maxFileBytes) {
    throw new Error("El archivo supera el límite de 100 MB.");
  }
};

const archiveInfo = async file => {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const entries = Object.values(zip.files).filter(entry => !entry.dir);
  if (entries.length > IMPORT_LIMITS.maxArchiveEntries) {
    throw new Error("El archivo comprimido contiene demasiadas entradas.");
  }
  let expandedBytes = 0;
  entries.forEach(entry => {
    const size = entry._data && Number(entry._data.uncompressedSize);
    if (Number.isFinite(size)) expandedBytes += size;
  });
  if (expandedBytes > IMPORT_LIMITS.maxExpandedBytes) {
    throw new Error("El archivo expandido supera el límite de 500 MB.");
  }
  return { zip, entries, expandedBytes };
};

const shapefileGroups = entries => {
  const groups = new Map();
  entries.forEach(entry => {
    const ext = extensionOf(entry.name);
    if (![".shp", ".dbf", ".prj", ".cpg", ".shx"].includes(ext)) return;
    const base = withoutExtension(normalizePath(entry.name));
    const key = base.toLowerCase();
    if (!groups.has(key)) groups.set(key, { key: datasetKey(base), base, files: {} });
    groups.get(key).files[ext.slice(1)] = entry;
  });
  return Array.from(groups.values()).filter(group => group.files.shp);
};

const parseGeoMetadata = metadata => {
  const raw = (metadata.key_value_metadata || []).find(item => item.key === "geo");
  if (!raw || !raw.value) return null;
  try { return JSON.parse(raw.value); } catch (error) { throw new Error("Los metadatos GeoParquet no son JSON válido."); }
};

export async function inspectImportedFile(file) {
  ensureFileLimit(file);
  const extension = extensionOf(file.name);
  if (extension === ".zip") {
    const { entries } = await archiveInfo(file);
    const groups = shapefileGroups(entries);
    if (!groups.length) throw new Error("El ZIP no contiene ningún archivo .shp.");
    return { format: "shapefile", datasets: groups.map(group => ({ key: group.key, name: group.base.split("/").pop() })) };
  }
  if (extension === ".shp") {
    return { format: "shapefile", needsCrs: true, datasets: [{ key: "dataset", name: withoutExtension(file.name) }] };
  }
  if (extension === ".parquet") {
    const buffer = await file.arrayBuffer();
    const metadata = await parquetMetadataAsync(asyncBuffer(buffer));
    const schema = parquetSchema(metadata);
    const geo = parseGeoMetadata(metadata);
    const columns = (schema.children || []).map(item => item.element.name);
    return {
      format: geo ? "geoparquet" : "parquet",
      columns,
      rowCount: Number(metadata.num_rows),
      geo,
      primaryGeometry: geo && geo.primary_column,
      needsParquetMapping: !geo,
    };
  }
  const formats = {
    ".kmz": "kmz", ".kml": "kml", ".gpx": "gpx", ".geojson": "geojson", ".json": "geojson",
  };
  if (!formats[extension]) throw new Error("Formato de archivo no soportado.");
  return { format: formats[extension], datasets: [{ key: "dataset", name: withoutExtension(file.name) }] };
}

const countCoordinates = geometry => {
  if (!geometry || !geometry.coordinates) return 0;
  const count = value => Array.isArray(value) && typeof value[0] === "number"
    ? 1
    : (Array.isArray(value) ? value.reduce((total, child) => total + count(child), 0) : 0);
  return count(geometry.coordinates);
};

const buildDataset = ({ key, name, features, sourceCrs = "EPSG:4326", warnings = [], cleanup = null, options = {} }) => {
  if (!features || !features.length) throw new Error(`La capa ${name} no contiene geometrías utilizables.`);
  if (features.length > IMPORT_LIMITS.maxFeatures) throw new Error(`La capa ${name} supera el límite de 200.000 entidades.`);
  const geometryTypes = new Set();
  let coordinateCount = 0;
  const addGeometryTypes = geometry => {
    if (!geometry) return;
    if (geometry.getType() === "GeometryCollection" && typeof geometry.getGeometries === "function") {
      geometry.getGeometries().forEach(addGeometryTypes);
      return;
    }
    geometryTypes.add(geometry.getType());
  };
  features.forEach(feature => {
    const geometry = feature.getGeometry && feature.getGeometry();
    if (geometry) {
      addGeometryTypes(geometry);
      coordinateCount += countCoordinates(geojsonFormat.writeGeometryObject(geometry));
    }
  });
  if (coordinateCount > IMPORT_LIMITS.maxCoordinates) throw new Error(`La capa ${name} supera el límite geométrico permitido.`);
  const allWarnings = warnings.slice();
  if (features.length > IMPORT_LIMITS.warningFeatures) allWarnings.push("La capa contiene más de 50.000 entidades y puede tardar en dibujarse.");
  return {
    key: datasetKey(key), name, features, cleanup, options, warnings: allWarnings,
    sourceCrs, geometryTypes: Array.from(geometryTypes), featureCount: features.length,
  };
};

const featuresFromGeoJSON = (geojson, dataProjection = "EPSG:4326") => geojsonFormat.readFeatures(geojson, {
  dataProjection,
  featureProjection: "EPSG:3857",
});

const parseShapefile = async (file, options) => {
  const parsed = await runBinaryWorker(file, "shapefile", options);
  return parsed.datasets.map(dataset => buildDataset({
    ...dataset,
    features: featuresFromGeoJSON(dataset.geojson, dataset.sourceCrs || "EPSG:4326"),
  }));
};

const unsupportedKmlElements = xml => {
  const unsupported = new Set();
  const names = new Set(["NetworkLink", "GroundOverlay", "ScreenOverlay", "PhotoOverlay", "Model", "Tour"]);
  Array.from(xml.getElementsByTagName("*")).forEach(node => {
    if (names.has(node.localName)) unsupported.add(node.localName);
  });
  return Array.from(unsupported).map(name => `${name} no se representa en el mapa 2D.`);
};

const parseXml = text => {
  const xml = new DOMParser().parseFromString(text, "application/xml");
  if (xml.getElementsByTagName("parsererror").length) throw new Error("El XML del archivo no es válido.");
  return xml;
};

const dirname = path => normalizePath(path).split("/").slice(0, -1).join("/");
const parseKml = async file => {
  let kmlText;
  let chosenKml = "";
  let iconUrls = new Map();
  const createdUrls = [];
  const warnings = [];
  if (extensionOf(file.name) === ".kmz") {
    const { zip, entries } = await archiveInfo(file);
    const kmlEntries = entries.filter(entry => extensionOf(entry.name) === ".kml");
    if (!kmlEntries.length) throw new Error("El KMZ no contiene ningún archivo KML.");
    const doc = kmlEntries.find(entry => normalizePath(entry.name).toLowerCase() === "doc.kml");
    const selected = doc || kmlEntries[0];
    chosenKml = normalizePath(selected.name);
    if (kmlEntries.length > 1) warnings.push(`El KMZ contiene ${kmlEntries.length} archivos KML; se utilizó ${chosenKml}.`);
    kmlText = await selected.async("text");
    const xml = parseXml(kmlText);
    const base = dirname(chosenKml);
    const hrefs = Array.from(xml.getElementsByTagName("href")).map(node => (node.textContent || "").trim()).filter(Boolean);
    for (const href of hrefs) {
      if (/^(https?:|data:|blob:)/i.test(href)) continue;
      const path = normalizePath(`${base}/${decodeURIComponent(href)}`);
      const entry = zip.file(path);
      if (!entry) {
        warnings.push(`No se encontró el recurso interno ${href}.`);
        continue;
      }
      const url = URL.createObjectURL(await entry.async("blob"));
      createdUrls.push(url);
      iconUrls.set(href, url);
      iconUrls.set(path, url);
    }
  } else {
    kmlText = await file.text();
  }
  const xml = parseXml(kmlText);
  warnings.push(...unsupportedKmlElements(xml));
  const format = new KML({ iconUrlFunction: href => iconUrls.get(href) || iconUrls.get(normalizePath(`${dirname(chosenKml)}/${href}`)) || href });
  const features = format.readFeatures(kmlText, { featureProjection: "EPSG:3857" });
  if (!features.length) {
    createdUrls.forEach(url => URL.revokeObjectURL(url));
    if (xml.getElementsByTagNameNS("*", "NetworkLink").length) {
      throw new Error("El KML/KMZ no contiene geometrías: solamente enlaza contenido externo mediante NetworkLink. Descargue desde el origen un KML/KMZ con las geometrías incluidas.");
    }
    throw new Error("El KML/KMZ no contiene geometrías compatibles para desplegar.");
  }
  return [buildDataset({
    key: "dataset", name: withoutExtension(file.name), features, warnings,
    cleanup: () => createdUrls.forEach(url => URL.revokeObjectURL(url)),
  })];
};

const parseGpx = async file => [buildDataset({
  key: "dataset", name: withoutExtension(file.name),
  features: new GPX().readFeatures(await file.text(), { featureProjection: "EPSG:3857" }),
})];

const parseGeoJson = async (file, options) => {
  let content;
  try { content = JSON.parse(await file.text()); } catch (error) { throw new Error("El archivo GeoJSON no contiene JSON válido."); }
  const sourceCrs = options.sourceCrs || "EPSG:4326";
  return [buildDataset({ key: "dataset", name: withoutExtension(file.name), features: featuresFromGeoJSON(content, sourceCrs), sourceCrs })];
};

const parseParquet = async (file, options) => {
  const parsed = await runBinaryWorker(file, "parquet", options);
  return {
    format: parsed.format,
    datasets: parsed.datasets.map(dataset => buildDataset({
      ...dataset,
      features: featuresFromGeoJSON(dataset.geojson),
    })),
  };
};

export async function parseImportedFile(file, options = {}) {
  ensureFileLimit(file);
  const extension = extensionOf(file.name);
  if ([".zip", ".shp"].includes(extension)) return { format: "shapefile", datasets: await parseShapefile(file, options) };
  if ([".kmz", ".kml"].includes(extension)) return { format: extension.slice(1), datasets: await parseKml(file) };
  if (extension === ".gpx") return { format: "gpx", datasets: await parseGpx(file) };
  if ([".geojson", ".json"].includes(extension)) return { format: "geojson", datasets: await parseGeoJson(file, options) };
  if (extension === ".parquet") return parseParquet(file, options);
  throw new Error("Formato de archivo no soportado.");
}

export function importManifest(parsed, displayName) {
  const multiple = parsed.datasets.length > 1;
  return {
    version: 1,
    format: parsed.format,
    datasets: parsed.datasets.map(dataset => ({
      key: dataset.key,
      name: multiple ? `${displayName} — ${dataset.name}` : displayName,
      sourceCrs: dataset.sourceCrs,
      geometryTypes: dataset.geometryTypes,
      featureCount: dataset.featureCount,
      options: dataset.options,
    })),
    warnings: Array.from(new Set(parsed.datasets.reduce((all, dataset) => all.concat(dataset.warnings || []), []))),
  };
}
